import { evaluateConditions } from './evaluateConditions';

/**
 * Validates dynamic document field values against their field definitions.
 * @param {Array} fields - Array of field definitions: { field_key, label, type, required, options }
 * @param {Object} fieldValues - Object containing field values: { [field_key]: value }
 * @param {Object} fieldFiles - Object containing raw File objects for file fields: { [field_key]: File }
 * @returns {Object} - { valid: boolean, errors: { [field_key]: string } }
 */
export const validateDocumentValues = (
  fields,
  fieldValues,
  fieldFiles = {}
) => {
  const errors = {};
  let valid = true;

  if (!fields || !Array.isArray(fields)) {
    return { valid, errors };
  }

  const visibleFields = fields.filter(f =>
    evaluateConditions(f.conditions, fieldValues)
  );

  visibleFields.forEach(field => {
    const { field_key, label, type, required, options } = field;
    const value = fieldValues[field_key];

    if (!required) return;

    if (type === 'file') {
      if (!fieldFiles[field_key]) {
        errors[field_key] = `${label || field_key}: file is required`;
        valid = false;
      }
    } else if (type === 'file_or_text') {
      if (!value || !value.mode) {
        errors[field_key] = `${label || field_key}: please choose text or file`;
        valid = false;
      } else if (
        value.mode === 'text' &&
        (!value.value || value.value.trim() === '')
      ) {
        errors[field_key] = `${
          label || field_key
        }: text description is required`;
        valid = false;
      } else if (value.mode === 'file' && !fieldFiles[field_key]) {
        errors[field_key] = `${label || field_key}: file is required`;
        valid = false;
      }
    } else {
      const isValueEmpty =
        value === undefined ||
        value === null ||
        (typeof value === 'string' && value.trim() === '');
      if (type === 'checkbox') {
        if (!value) {
          errors[field_key] = `${label || field_key} is required.`;
          valid = false;
        }
      } else if (isValueEmpty) {
        errors[field_key] = `${label || field_key} is required.`;
        valid = false;
      } else {
        // If there is a value, perform type-specific validation
        if (type === 'number') {
          const num = Number(value);
          if (isNaN(num)) {
            errors[field_key] = `${label || field_key} must be a valid number.`;
            valid = false;
          }
        } else if (type === 'date') {
          // Simple YYYY-MM-DD regex check
          const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
          if (!dateRegex.test(value) && isNaN(Date.parse(value))) {
            errors[field_key] = `${label || field_key} must be a valid date.`;
            valid = false;
          }
        } else if (type === 'select' || type === 'radio') {
          if (options && Array.isArray(options)) {
            if (!options.includes(value)) {
              errors[field_key] = `${
                label || field_key
              } has an invalid selection.`;
              valid = false;
            }
          }
        }
      }
    }
  });

  return { valid, errors };
};

export default validateDocumentValues;
